export const sampleBookMeta = {
  title: "The Martinez Family",
  subtitle: "Our Chaos in Texts",
  year: 2024,
  yearbookType: "family",
  dedication: "To the group chat that nobody can mute but everyone secretly loves — this book is proof that family is just a group project nobody signed up for.",
};

export const sampleCharacters = [
  {
    name: "Gloria",
    bio: "Replies to messages from three days ago as if the conversation never moved on. Sends voice notes for things that could be one word. Has never once successfully attached a photo on the first try. Will ask 'who's coming to Sunday dinner?' every single week until the heat death of the universe.",
    traits: ["Voice Note Queen", "Time Traveler", "Sunday Dinner Enforcer"],
    signature_quote: "Did anyone see my message???",
    story_count: 14,
  },
  {
    name: "Carlos",
    bio: "Believes he was put on this earth to deliver dad jokes to an unwilling audience. Types in ALL CAPS because he accidentally turned it on in 2019 and never figured out how to turn it off. Sends articles with the caption 'interesting' and no further context.",
    traits: ["Dad Joke Machine", "CAPS LOCK Enthusiast", "Link Forwarder"],
    signature_quote: "WHY IS NOBODY LAUGHING",
    story_count: 14,
  },
  {
    name: "Rosa",
    bio: "The family's accidental comedian. Sends messages clearly meant for someone else, reacts to sad news with the laughing emoji, and has roasted every family member at least twice without realizing it. Once ended an argument with 'ok mijo' and it became a family meme.",
    traits: ["Accidental Savage", "Wrong Chat Expert", "Emoji Wildcard"],
    signature_quote: "ok mijo",
    story_count: 10,
  },
  {
    name: "Diego",
    bio: "The eldest child who communicates primarily through sarcasm and GIF reactions. Will roast you for a typo and then quietly Venmo you rent money. Appointed himself the family IT department and regrets it daily.",
    traits: ["Chief Roaster", "Reluctant IT Support", "Secret Softie"],
    signature_quote: "I'm not googling that for you again",
    story_count: 14,
  },
  {
    name: "Sofia",
    bio: "Sends seventeen messages in a row instead of one paragraph. Every minor life event is a five-act drama. Uses so many emojis that her messages look like hieroglyphics. Once changed the group name to 'Sofia's Support Group' and nobody had the heart to change it back for two weeks.",
    traits: ["Serial Texter", "Drama Narrator", "Emoji Hieroglyphist"],
    signature_quote: "GUYS. GUYS. YOU WON'T BELIEVE THIS.",
    story_count: 14,
  },
];

export const sampleChapters = [
  { index: 0, title: "Mom's Phone Emergency", title_emoji: "\u{1F4F1}", date: "Jan 8, 2024", is_sample: true },
  { index: 1, title: "Dad Discovers GIFs", title_emoji: "\u{1F60E}", date: "Jan 22, 2024", is_sample: false },
  { index: 2, title: "The WiFi Password Saga", title_emoji: "\u{1F4F6}", date: "Feb 5, 2024", is_sample: false },
  { index: 3, title: "Sunday Dinner Wars", title_emoji: "\u{1F372}", date: "Feb 18, 2024", is_sample: true },
  { index: 4, title: "Abuela's Wrong Chat", title_emoji: "\u{1F62C}", date: "Mar 3, 2024", is_sample: false },
  { index: 5, title: "The Family Photo Attempt", title_emoji: "\u{1F4F8}", date: "Mar 17, 2024", is_sample: false },
  { index: 6, title: "Diego's IT Meltdown", title_emoji: "\u{1F4BB}", date: "Apr 2, 2024", is_sample: false },
  { index: 7, title: "Sofia's Parking Ticket Saga", title_emoji: "\u{1F697}", date: "Apr 20, 2024", is_sample: false },
  { index: 8, title: "Dad's Inspirational Phase", title_emoji: "\u{1F4AA}", date: "May 6, 2024", is_sample: false },
  { index: 9, title: "The Recipe Rebellion", title_emoji: "\u{1F373}", date: "May 22, 2024", is_sample: false },
  { index: 10, title: "Thanksgiving Seating Wars", title_emoji: "\u{1F983}", date: "Jun 10, 2024", is_sample: false },
  { index: 11, title: "Abuela vs. Autocorrect", title_emoji: "\u{1F605}", date: "Jul 4, 2024", is_sample: false },
  { index: 12, title: "The Vacation Planning Disaster", title_emoji: "\u{2708}\u{FE0F}", date: "Aug 15, 2024", is_sample: true },
  { index: 13, title: "Carlos's Birthday Surprise Fail", title_emoji: "\u{1F382}", date: "Sep 8, 2024", is_sample: false },
  { index: 14, title: "The Christmas List Catastrophe", title_emoji: "\u{1F384}", date: "Oct 30, 2024", is_sample: false },
];

export const sampleStories = [
  {
    story_id: "sample-family-001",
    chapter_index: 0,
    title: "Mom's Phone Emergency",
    title_emoji: "\u{1F4F1}",
    date: "January 8, 2024",
    time_range: "9:14 AM - 12:03 PM",
    participants: ["Gloria", "Carlos", "Rosa", "Diego", "Sofia"],
    tags: ["Tech Support", "Panic", "Classic Gloria"],
    narrative: `It was a quiet Monday morning in the Martinez family chat until [[Gloria|Gloria]] dropped a message at 9:14 AM that would derail everyone's day.

"HELP. My phone is broken. Everything is in Spanish now."

[[Diego|Diego]] responded first: "Mom. You're Mexican. Your phone has always been in Spanish."

"NO. It's in DIFFERENT Spanish. The wrong Spanish."

[[Sofia|Sofia]] immediately sent seven messages in a row: "WHAT" "happened" "did you drop it" "in water??" "omg" "wait" "what do you mean wrong Spanish"

[[Carlos|Carlos]] entered the chat in his signature style: "MAYBE THE PHONE FINALLY LEARNED SPANISH BETTER THAN YOU." Then, two seconds later: "THAT WAS A JOKE." Then: "GLORIA DON'T BE MAD."

[[Gloria|Gloria]] sent a screenshot. The phone was, in fact, in Portuguese. Nobody in the family speaks Portuguese.

"See??? WRONG SPANISH," she wrote triumphantly.

[[Diego|Diego]] tried to walk her through the settings. "Go to Settings, then General, then Language." What followed was forty-five minutes of the most painful tech support in recorded history. Gloria sent fourteen screenshots, none of which were the right screen. She accidentally turned on VoiceOver. She enabled bold text. At one point, her phone was upside down.

"Mom, how is it upside down? It's a screenshot," [[Diego|Diego]] wrote.

"DON'T YELL AT YOUR MOTHER," replied [[Carlos|Carlos]].

"I'm not yelling, I'm typing in lowercase, which is more than you've ever done."

[[Rosa|Rosa]] surfaced at 11:47 AM with a message that had absolutely nothing to do with anything: "The chicken is ready." Then a thumbs-up emoji. Then silence.

"Abuela, what chicken?" asked [[Sofia|Sofia]].

No response.

[[Gloria|Gloria]] finally sent "I FIXED IT" at 12:03 PM. She had not fixed it. She had switched the phone to French. But everyone was too exhausted to say anything, and [[Diego|Diego]] quietly added "Family IT Department" to his bio with the resigned energy of a man who knows this will happen again next week.`,
  },
  {
    story_id: "sample-family-003",
    chapter_index: 3,
    title: "Sunday Dinner Wars",
    title_emoji: "\u{1F372}",
    date: "February 18, 2024",
    time_range: "2:00 PM - 7:22 PM",
    participants: ["Gloria", "Carlos", "Rosa", "Diego", "Sofia"],
    tags: ["Sunday Dinner", "Passive Aggressive", "The Usual"],
    narrative: `Every week, [[Gloria|Gloria]] asks the same question. Every week, chaos follows. This Sunday was no different.

"Who's coming to dinner Sunday?" she posted at 2:00 PM sharp — as if she didn't ask this same question every single week since the invention of the group chat.

Silence. For forty-seven minutes, absolute silence.

"Hello???" she followed up. "I can see you're all online."

[[Sofia|Sofia]] was first: "Me!! Obviously!! What are we having??"

"Mole," said [[Gloria|Gloria]].

"WAIT actually can you make enchiladas instead?" [[Sofia|Sofia]] asked. Then: "or both??" Then: "actually mole is fine." Then: "no wait. Enchiladas." Then: "you know what, surprise me."

[[Diego|Diego]] replied with just: "Depends. Is Tía Luisa coming?"

"Why does that matter?" asked [[Gloria|Gloria]].

"Because last time she told me I look tired and then asked if I'm eating enough. I was literally at a dinner table. Eating."

[[Carlos|Carlos]] entered with: "I WILL BE THERE. I LIVE THERE."

"Carlos, you don't have to RSVP to your own house," [[Gloria|Gloria]] responded.

"I'M BEING POLITE."

The conversation took a turn when [[Gloria|Gloria]] casually mentioned she was thinking of trying a new mole recipe. The chat went nuclear.

"NEW recipe??" [[Sofia|Sofia]] wrote. "What was wrong with the old recipe?? The old recipe is PERFECT."

"It's Abuela's recipe, you can't just change Abuela's recipe," [[Diego|Diego]] added.

[[Rosa|Rosa]], who had been silent the entire time, materialized with the message: "My recipe is fine the way it is."

Then: "Don't touch my recipe."

Then: "ok mijo"

Nobody knew what the "ok mijo" was for. It didn't matter. The authority had spoken.

[[Gloria|Gloria]] abandoned the new recipe idea immediately. "I was JUST saying. I'm making the regular mole. Everybody relax."

"Are we relaxed?" [[Diego|Diego]] asked.

"WE ARE RELAXED," confirmed [[Carlos|Carlos]].

At 6:45 PM, Gloria sent a photo of the finished mole with the caption "For my ungrateful children who don't even RSVP on time." [[Sofia|Sofia]] responded with eighteen heart emojis. [[Diego|Diego]] sent a single thumbs up, which [[Gloria|Gloria]] interpreted as hostility and required another thirty minutes of reassurance that he was, in fact, excited about the mole.

The final message of the night was from [[Rosa|Rosa]] at 7:22 PM: "The mole was better last time." No one dared respond.`,
  },
  {
    story_id: "sample-family-012",
    chapter_index: 12,
    title: "The Vacation Planning Disaster",
    title_emoji: "\u{2708}\u{FE0F}",
    date: "August 15, 2024",
    time_range: "10:00 AM - 11:55 PM",
    participants: ["Gloria", "Carlos", "Rosa", "Diego", "Sofia"],
    tags: ["Vacation", "Democracy Failed", "Budget Horror"],
    narrative: `[[Gloria|Gloria]] started the thread at 10:00 AM with a message so optimistic it was almost painful: "Family vacation this year!! Let's pick a place. Everyone gets one vote!"

Democracy lasted approximately four minutes.

[[Sofia|Sofia]] immediately sent: "CANCÚN" then "or Tulum" then "wait no Cancún" then "actually what about both" then "Cancún final answer." Five messages. One thought.

[[Diego|Diego]] voted for "literally anywhere with WiFi and no sharing a room with Dad."

"WHAT'S WRONG WITH SHARING A ROOM WITH ME," [[Carlos|Carlos]] typed.

"You snore so loud the hotel called last time."

"THAT WAS ONE TIME." Then: "ALSO IT WAS SLEEP APNEA. I HAVE A MACHINE NOW."

[[Rosa|Rosa]] voted for "the place with the nice garden." Nobody knew what place she meant. When asked, she sent a photo of a garden that could have been anywhere on earth. The metadata said it was from 2016.

"Abuela, where is this?" asked [[Sofia|Sofia]].

"Beautiful, right?" replied [[Rosa|Rosa]]. This was not an answer.

By noon, the chat had devolved into a budget argument. [[Gloria|Gloria]] had found an all-inclusive resort and sent the price per person. [[Diego|Diego]] replied with a single message: "Mom, I can't afford this. I am not a doctor. I fix code for a living."

"You went to college," [[Gloria|Gloria]] pointed out, as if this was a counterargument.

"College is WHY I can't afford it."

[[Carlos|Carlos]] tried to restore order: "WHAT IF WE DO A ROAD TRIP INSTEAD. VERY AFFORDABLE. FAMILY BONDING." He then sent a Google Maps screenshot of a 22-hour drive to Yellowstone.

"Dad, that's a 22-hour drive," [[Diego|Diego]] noted.

"THAT'S THE BONDING PART."

"I will not survive 22 hours in a car with this family," [[Sofia|Sofia]] declared. "I love you all but I will open the door on the highway."

The conversation spiraled for hours. [[Gloria|Gloria]] sent seven different resort links. [[Carlos|Carlos]] countered each one with a campground option. [[Sofia|Sofia]] kept raising the budget by sending five-star hotel photos with the caption "just saying." [[Diego|Diego]] built an actual spreadsheet and shared it. Nobody opened the spreadsheet.

At 9 PM, [[Rosa|Rosa]] sent: "I'm not going anywhere. My shows are on."

"Abuela, your shows are on every day," [[Sofia|Sofia]] wrote.

"Exactly."

At 11:55 PM, [[Gloria|Gloria]] made an executive decision: "We're going to the beach house in Puerto Vallarta like every year. This conversation is over. Goodnight."

[[Diego|Diego]] replied: "So why did we vote?"

[[Carlos|Carlos]] sent: "GOODNIGHT EVERYONE I LOVE YOU ALL." Then: "EVEN DIEGO."

"Especially me," [[Diego|Diego]] corrected.

[[Sofia|Sofia]] closed the thread: "wait so is it all-inclusive or do I need to pack snacks because that changes everything"

Nobody answered. The family chat went dark. They'd be back at it by Tuesday.`,
  },
];
